import { useEffect, useState } from 'react';
import { clientFetch } from './api';
import ClientShell from './ClientShell';

const styles = {
  container: {
    padding: '24px',
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '32px',
  },
  summaryCard: {
    backgroundColor: '#111827',
    border: '1px solid #1F2937',
    borderRadius: '8px',
    padding: '16px',
    color: '#FFF',
  },
  summaryLabel: {
    fontSize: '13px',
    color: '#9CA3AF',
    fontWeight: '600',
    marginBottom: '8px',
  },
  summaryValue: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#3B82F6',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    borderSpacing: '0',
  },
  tableHeader: {
    borderBottom: '2px solid #1F2937',
  },
  tableHeaderCell: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#9CA3AF',
    backgroundColor: '#111827',
  },
  tableRow: {
    borderBottom: '1px solid #1F2937',
    transition: 'background-color 0.2s',
  },
  tableRowHover: {
    backgroundColor: '#0f172a',
  },
  tableCell: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#FFF',
  },
  invoiceNumber: {
    fontWeight: '600',
    color: '#3B82F6',
    cursor: 'pointer',
  },
  amount: {
    fontWeight: '600',
    color: '#FFF',
  },
  badge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
  },
  badgePaid: {
    backgroundColor: '#10b981',
    color: '#fff',
  },
  badgeSent: {
    backgroundColor: '#f59e0b',
    color: '#1f2937',
  },
  badgeOverdue: {
    backgroundColor: '#ef4444',
    color: '#fff',
  },
  loadingText: {
    color: '#9CA3AF',
    fontSize: '14px',
  },
  errorText: {
    color: '#EF4444',
    fontSize: '14px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
    color: '#9CA3AF',
  },
};

export default function ClientInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [totals, setTotals] = useState({
    paid: 0,
    outstanding: 0,
    overdue: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        setLoading(true);
        const data = await clientFetch('/client/portal/invoices');
        setInvoices(Array.isArray(data) ? data : data.invoices || []);
        setTotals(data.totals || { paid: 0, outstanding: 0, overdue: 0 });
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to load invoices');
        console.error('Invoices error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, []);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <span style={{ ...styles.badge, ...styles.badgePaid }}>Paid</span>;
      case 'sent':
        return <span style={{ ...styles.badge, ...styles.badgeSent }}>Sent</span>;
      case 'overdue':
        return <span style={{ ...styles.badge, ...styles.badgeOverdue }}>Overdue</span>;
      default:
        return <span style={{ ...styles.badge }}>{status}</span>;
    }
  };

  if (loading) {
    return (
      <ClientShell title="Invoices">
        <div style={styles.container}>
          <p style={styles.loadingText}>Loading invoices...</p>
        </div>
      </ClientShell>
    );
  }

  if (error) {
    return (
      <ClientShell title="Invoices">
        <div style={styles.container}>
          <p style={styles.errorText}>Error: {error}</p>
        </div>
      </ClientShell>
    );
  }

  return (
    <ClientShell title="Invoices">
      <div style={styles.container}>
        {/* Summary Cards */}
        <div style={styles.summaryGrid}>
          <div style={styles.summaryCard}>
            <div style={styles.summaryLabel}>Total Paid</div>
            <div style={styles.summaryValue}>${totals.paid?.toFixed(2) || '0.00'}</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryLabel}>Outstanding</div>
            <div style={styles.summaryValue}>${totals.outstanding?.toFixed(2) || '0.00'}</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryLabel}>Overdue</div>
            <div style={styles.summaryValue}>${totals.overdue?.toFixed(2) || '0.00'}</div>
          </div>
        </div>

        {/* Invoices Table */}
        {invoices && invoices.length > 0 ? (
          <table style={styles.table}>
            <thead style={styles.tableHeader}>
              <tr>
                <th style={styles.tableHeaderCell}>Invoice Number</th>
                <th style={styles.tableHeaderCell}>Amount</th>
                <th style={styles.tableHeaderCell}>Status</th>
                <th style={styles.tableHeaderCell}>Due Date</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr
                  key={invoice.id}
                  style={styles.tableRow}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#0f172a';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <td style={{ ...styles.tableCell, ...styles.invoiceNumber }}>
                    {invoice.invoiceNumber}
                  </td>
                  <td style={{ ...styles.tableCell, ...styles.amount }}>
                    ${invoice.amount?.toFixed(2) || '0.00'}
                  </td>
                  <td style={styles.tableCell}>{getStatusBadge(invoice.status)}</td>
                  <td style={styles.tableCell}>{invoice.dueDate}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={styles.emptyState}>
            <p>No invoices found</p>
          </div>
        )}
      </div>
    </ClientShell>
  );
}
